import { inject, Injectable } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { Location } from "@angular/common";
import { map } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";

import { TppServiceBeneficiaryDTO, TPPServiceDefinitionDTO } from "@shared/models/tpp-service.interface";
import { TppApiServiceService } from "@modules/tpp-catalogue/_services/api/tpp-api-service.service";
import { ToastService } from "@shared/services/toast/toast.service";

import { SettlementAccount, SettlementAccountPagination } from "src/app/shared/models/settlement-account.interface";
import { environment as env } from "@environments/environment";
import { SelectOption } from "@shared/models/select-option.interface";
import {
	SourceFoundAccount,
	SourceFoundAccountPagination
} from "@modules/service-definition/_models/source-accounts.interface";
import { TppServiceDefinitionService } from "@modules/service-definition/_services/tpp-service-definition/tpp-service-definition.service";

@Injectable({
	providedIn: "root"
})
export class TppCommonServiceService {
	setSettlementAccountOptions$ = new BehaviorSubject<SelectOption[]>([]);

	private tppApiService = inject(TppApiServiceService);
	private toastService = inject(ToastService);
	private location = inject(Location);
	private http = inject(HttpClient);
	private tppServiceDefinitionService = inject(TppServiceDefinitionService);

	saveNewBeneficiary(
		formData: { account_name: string; country: string; currency: string },
		fields: Record<string, AbstractControl>,
		serviceId: string,
		isEdit?: boolean,
		subserviceId?: string,
		paygroupId?: string,
		skipValidation = false,
		accountId?: string,
		accountVersion?: string,
		benId?: string,
		benVersion?: string
	) {
		const rawFormData = formData;

		let data: TppServiceBeneficiaryDTO;

		if (isEdit) {
			data = {
				id: accountId,
				version: accountVersion,
				service: serviceId,
				country: rawFormData.country,
				name: rawFormData.account_name,
				currency: rawFormData.currency,

				bankAccount: {
					id: benId,
					version: benVersion,
					bankName: fields.bankName.value,
					country: rawFormData.country,
					currency: rawFormData.currency,
					fields: [],
					skipValidation
				}
			};
		} else {
			data = {
				service: serviceId,
				country: rawFormData.country,
				name: rawFormData.account_name,
				currency: rawFormData.currency,

				bankAccount: {
					bankName: fields.bankName.value,
					country: rawFormData.country,
					currency: rawFormData.currency,
					fields: [],
					skipValidation
				}
			};
		}

		Object.keys(fields).forEach(field => {
			if (
				field !== "bankName" &&
				field !== "account_name" &&
				field !== "country" &&
				field !== "currency" &&
				field !== "client_id" &&
				fields[field].value !== null &&
				fields[field].value !== undefined &&
				fields[field].value !== ""
			) {
				data.bankAccount.fields.push({
					key: field,
					value: fields[field].value
				});
			}
		});

		if (isEdit) {
			this.tppApiService.updateTPPAccount(data).subscribe({
				next: () => {
					this.toastService.showSuccess("Account updated successfully");

					//this.router.navigate(["dashboard/tpp-catalogue/add-new-service"]);
					this.location.back();
				},
				error: () => {
					this.toastService.showError("Error updating your account, please try again");
				}
			});
		} else {
			this.tppApiService.createTPPAccount(data).subscribe({
				next: data => {
					this.toastService.showSuccess("Account created successfully");

					//this.router.navigate(["dashboard/tpp-catalogue/add-new-service"]);

					//save new val
					this.tppServiceDefinitionService
						.saveToDefinitionWithNewBeneficaryValue(
							data,
							subserviceId ? subserviceId : "",
							paygroupId ? paygroupId : ""
						)
						.subscribe(() => {
							this.location.back();
						});
				},
				error: () => {
					this.toastService.showError("Error updating your creating, please try again");
				}
			});
		}
	}

	searchSettlementAccountsByLegalEntityId(
		legalEntityId: string,
		currencies: string
	): Observable<SettlementAccount[]> {
		return this.http
			.get<SettlementAccountPagination>(
				`${env.apiUri}/v1/accounts/settlement?legalEntityId=${legalEntityId}&currencies=${currencies}&pageSize=-1`
			)
			.pipe(
				//we don't need the pagination data so only return the items array from the object

				map((res: SettlementAccountPagination) => {
					return res.items;
				})
			);
	}

	searchSourceOfFunds(legalEntityId: string, currencies: string): Observable<SourceFoundAccount[]> {
		return this.http
			.get<SourceFoundAccountPagination>(
				`${env.apiUri}/v1/accounts/source?legalEntityId=${legalEntityId}&currencies=${currencies}&paymentTypes=TPP`
			)
			.pipe(
				//we don't need the pagination data so only return the items array from the object
				map((res: SourceFoundAccountPagination) => {
					return res.items;
				})
			);
	}

	setSettlementAccountOptions(options: SelectOption[]) {
		this.setSettlementAccountOptions$.next(options);
	}

	editTPPServiceDefinition(formData: TPPServiceDefinitionDTO): Observable<TPPServiceDefinitionDTO> {
		return this.http.put<TPPServiceDefinitionDTO>(`${env.apiUri}/v1/tpp-service-definitions`, formData);
	}
}
